import React, { useEffect, useState } from 'react'
import { Box, Popover } from '@mui/material'

import { colors } from '~tokens/internal'
import { stripHash } from '~utils/colorUtils'

import ColorPicker from '../ColorPicker/ColorPicker'
import TextField from '../TextField'

import './ColorInput.scss'

export interface ColorInputProps {
  name: string
  label?: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  className?: string
  disablePicker?: boolean
}

const ColorInput: React.FC<ColorInputProps> = ({
  name,
  label,
  value,
  onChange,
  className,
  disablePicker = false,
}) => {
  const [hexInput, setHexInput] = useState(stripHash(value || ''))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [error, setError] = useState<string>('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    setHexInput(stripHash(value || ''))
  }, [value])

  const classNames = ['internal-colorinput', className].filter(Boolean).join(' ')

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = stripHash(e.target.value)
    const hexRegex = /^[0-9A-F]{0,6}$/

    if (hexRegex.test(inputValue)) {
      setHexInput(inputValue)
      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: inputValue,
        },
      })

      if (touched && inputValue.length === 6) {
        setError('')
      }
    }
  }

  const handleBlur = (): void => {
    setTouched(true)
    if (hexInput.length > 0 && hexInput.length !== 6) {
      setError('Enter a valid hex color code')
    } else {
      setError('')
    }
  }

  const handleColorPickerChange = (color: string): void => {
    const cleanColor = color.replace('#', '')
    setHexInput(cleanColor)
    onChange({
      target: {
        name,
        value: cleanColor,
      },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const displayColor = hexInput.length === 6 ? `#${hexInput}` : colors.neutral[200]

  return (
    <Box className={classNames}>
      <TextField
        name={name}
        label={label}
        value={hexInput}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder='000000'
        error={!!error}
        errorText={error}
        InputProps={{
          startAdornment: <Box className='internal-colorinput__prefix'>#</Box>,
          endAdornment: (
            <Box
              className='internal-colorinput__color-swatch'
              onClick={disablePicker ? undefined : handleClick}
              sx={{ '--swatch-bg': displayColor, cursor: disablePicker ? 'default' : 'pointer' }}
            />
          ),
        }}
      />

      {!disablePicker && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <ColorPicker value={displayColor} onChange={handleColorPickerChange} />
        </Popover>
      )}
    </Box>
  )
}

export default ColorInput
